import { gradeBenchPanelActions } from '@codametrix/ui-common';

const isErrorCode = (
  c: Accelerate.CQACode,
  idealCodeIds: Set<number>,
  completion: Record<string, boolean> | undefined
): boolean =>
  completion?.[String(c.id)] === true && !idealCodeIds.has(c.id);

export const showMoveErrorsForInvoice = (
  invoice: Accelerate.CQAInvoice,
  idealCodeIds: Set<number> | null | undefined,
  editable: boolean,
  codeCompletion?: Record<string, boolean>
): boolean =>
  editable &&
  idealCodeIds != null &&
  (invoice.transactions ?? []).some((tx: Accelerate.CQATransaction) =>
    [
      ...(tx.procedures ?? []),
      ...(tx.diagnoses ?? []),
      ...(tx.modifiers ?? [])
    ].some((c: Accelerate.CQACode) => isErrorCode(c, idealCodeIds, codeCompletion))
  );

export const isAlreadyAtBottomForInvoice = (
  invoice: Accelerate.CQAInvoice,
  idealCodeIds: Set<number> | null | undefined,
  codeCompletion?: Record<string, boolean>
): boolean => {
  if (!idealCodeIds) return true;
  const checkCodes = (codes: Accelerate.CQACode[]) => {
    const sorted = [...codes].sort((a, b) => a.sequenceNumber - b.sequenceNumber);
    let seenError = false;
    for (const c of sorted) {
      if (isErrorCode(c, idealCodeIds, codeCompletion)) {
        seenError = true;
      } else if (seenError) {
        return false;
      }
    }
    return true;
  };
  return (invoice.transactions ?? []).every((tx: Accelerate.CQATransaction) =>
    checkCodes(tx.procedures ?? []) &&
    checkCodes(tx.diagnoses ?? []) &&
    checkCodes(tx.modifiers ?? [])
  );
};

const partitionCodesByIdeal = (
  codes: Accelerate.CQACode[],
  idealCodeIds: Set<number>,
  codeCompletion?: Record<string, boolean>
): Accelerate.CQACode[] => {
  const sorted = [...codes].sort((a, b) => a.sequenceNumber - b.sequenceNumber);
  const errors = sorted.filter(c => isErrorCode(c, idealCodeIds, codeCompletion));
  const notErrors = sorted.filter(c => !isErrorCode(c, idealCodeIds, codeCompletion));
  return [...notErrors, ...errors].map((c, i) => ({ ...c, sequenceNumber: i + 1 }));
};

export const handleMoveErrorsForInvoice = (
  invoice: Accelerate.CQAInvoice,
  idealCodeIds: Set<number> | null | undefined,
  editable: boolean,
  dispatch: (action: any) => any,
  codeCompletion?: Record<string, boolean>
): void => {
  if (!idealCodeIds || !editable) return;
  for (const transaction of invoice.transactions ?? []) {
    const procedures = partitionCodesByIdeal(transaction.procedures ?? [], idealCodeIds, codeCompletion);
    const diagnoses = partitionCodesByIdeal(transaction.diagnoses ?? [], idealCodeIds, codeCompletion);
    const modifiers = partitionCodesByIdeal(transaction.modifiers ?? [], idealCodeIds, codeCompletion);
    const allOriginal = [
      ...(transaction.procedures ?? []),
      ...(transaction.diagnoses ?? []),
      ...(transaction.modifiers ?? [])
    ];
    const allResequenced = [...procedures, ...diagnoses, ...modifiers];
    const hasChanges = allResequenced.some(c => {
      const original = allOriginal.find(o => o.id === c.id);
      return original?.sequenceNumber !== c.sequenceNumber;
    });
    if (!hasChanges) continue;
    dispatch(gradeBenchPanelActions.bulkResequenceCodes({
      procedures,
      diagnoses,
      modifiers,
      context: { invoice, transaction }
    }) as any);
  }
};
